import { createContext, useContext, useEffect, useState } from "react";

export type ExpenseLimitBudget = {
  category: string;
  amount: number;
};

type ExpenseLimitByMonth = {
  [month: string]: ExpenseLimitBudget[];
};

type ExpenseLimitContextType = {
  expenseLimits: ExpenseLimitByMonth;
  updateExpenseLimit: (month: string, category: string, amount: number) => void;
  removeExpenseLimit: (month: string, category: string) => void;
};

const ExpenseLimitsContext = createContext<ExpenseLimitContextType | undefined>(
  undefined
);

export const ExpenseLimitsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [expenseLimits, setExpenseLimits] = useState<ExpenseLimitByMonth>(
    () => {
      const stored = localStorage.getItem("expenseLimits");
      return stored ? JSON.parse(stored) : {};
    }
  );

  useEffect(() => {
    localStorage.setItem("expenseLimits", JSON.stringify(expenseLimits));
  }, [expenseLimits]);

  const updateExpenseLimit = (
    month: string,
    category: string,
    amount: number
  ) => {
    setExpenseLimits((prev) => {
      const monthBudgets = prev[month] || [];
      const updated = monthBudgets.filter((b) => b.category !== category);
      return {
        ...prev,
        [month]: [...updated, { category, amount }],
      };
    });
  };

  const removeExpenseLimit = (month: string, category: string) => {
    setExpenseLimits((prev) => {
      const monthBudgets = prev[month] || [];
      const updateExpenseLimit = monthBudgets.filter(
        (b) => b.category !== category
      );

      return {
        ...prev,
        [month]: updateExpenseLimit,
      };
    });
  };

  return (
    <ExpenseLimitsContext.Provider
      value={{
        expenseLimits,
        updateExpenseLimit: updateExpenseLimit,
        removeExpenseLimit,
      }}
    >
      {children}
    </ExpenseLimitsContext.Provider>
  );
};

export const useExpenseLimits = (): ExpenseLimitContextType => {
  const context = useContext(ExpenseLimitsContext);
  if (!context)
    throw new Error(
      "useExpensLimits must be used within ExpenseLimitsProvider"
    );
  return context;
};
